import React, { useContext, useEffect, useState } from 'react';
import { Col, Row } from 'antd';
import styles from './farmOutlet.module.scss';
import DashboardSidebar from 'shared/components/DashboardSidebar';
import HeaderForStages from '../HeaderForStages';
import FooterForStages from '../FooterForStages';
import clsx from 'clsx';
import { CaretLeftOutlined } from '@ant-design/icons';
import HelpDesk from '../Helpdesk';
import { HelpDeskContext } from 'context/HelpDeskContext';
import { FlowContainerConstants } from 'views/FlowContainer/FlowContainerConstants';
import FarmCommodity from 'views/ActivityDataEntry/FarmCommodity';
import FarmboxService from 'services/Farmbox/farmbox.service';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form';
import useFarmboxRedirections from 'shared/utils/useFarmboxRedirections';
import { CommodityTabsKeys } from 'enums/FarmPageEnum';
import { useSubmitHandlers } from './farmFormManager';
import { FarmboxRawMaterialsData } from 'models/FarmboxRawMaterials/farmBoxRawMaterials.model';
import FarmboxCommodityDistributionService from 'services/Farmbox/CommodityDistribution/commodityDistribution.service';
import { yupResolver } from '@hookform/resolvers/yup';
import validationSchemaForCommodityDestribution from 'views/FarmCreationFlow/FarmCommodityDistribution/validationSchema';
import FarmboxRawMaterialsService from 'services/Farmbox/RawMaterials/farmboxRawMaterials.service';
import {
  FormDataTypes,
  useActiveTabChecks,
  useTypeGuards,
} from './farmOutletHelper';
import { FarmBoxCommodityDestribution } from 'models/FarmBoxCommodityDestribution/farmBoxCommodityDestribution.model';
import { TransportationDetails } from 'models/Farmbox/transportation.model';
import { FuelAndEnergyDetails } from 'models/Farmbox/fuelAndEnergy.model';
import { FieldDetails } from 'models/Farmbox/field.model';
import { farmFieldValidationSchema } from 'views/FarmCreationFlow/Fields/farmFieldFlow.validation';
import { farmTransportationValidationSchema } from 'views/FarmCreationFlow/Transportation/farmTransportationFlow.validation';
import { stage1InitialData } from './stage1FormHandlers';
import FarmTransportationService from 'services/FarmTransportationService/farmTransportation.service';
import FarmFuelAndEnergyService from 'services/FarmFuelAndEnergyService/farmFuelAndEnergy.service';
import FarmFieldService from 'services/FarmFieldService/farmField.service';
import { stage3InitialData } from './stage3FormHandlers';
import { stage4InitialData } from './stage4FormHandlers';
import { stage5InitialData } from './stage5FormHandlers';
import { validationSchema } from 'views/FarmCreationFlow/RawMaterials/validationSchema';
import { stage2InitialData } from './stage2FormHanlders';
import { Commodity } from 'models/Farmbox/commodity.model';
import { footerItems } from './footerConstants';

const FarmOutlet = () => {
  const { redirectToFarm } = useFarmboxRedirections();
  const { isHelpDeskOpen, setIsHelpDeskOpen, helpDeskData, helpDeskLoading } =
    useContext(HelpDeskContext);
  const {
    loading: commodityLoading,
    getCommodity,
    commodity,
  } = FarmboxService();
  const { loading: rawMaterialLoading, getRawMaterials } =
    FarmboxRawMaterialsService();
  const { loading: commoditydistributionLoading, getCommodityDistribution } =
    FarmboxCommodityDistributionService();
  const { loading: transportationLoading, fetchTransportationData } =
    FarmTransportationService();
  const { loading: fuelAndEnergyLoading, fetchFuelAndEnergyData } =
    FarmFuelAndEnergyService();
  const { loading: fieldLoading, fetchFieldData } = FarmFieldService();

  const isLoading =
    commodityLoading ||
    rawMaterialLoading ||
    commoditydistributionLoading ||
    transportationLoading ||
    fuelAndEnergyLoading ||
    fieldLoading;

  const { id: commodityId, status } = useParams();
  const location = useLocation();
  const activeTabChecks = useActiveTabChecks(location);
  const {
    isField,
    isFuelAndEnergy,
    isRawMaterial,
    isTransportation,
    isOverview,
  } = activeTabChecks;
  const isNotOverview = !isOverview;
  const typeGuards = useTypeGuards();
  const {
    isRawMaterialsData,
    isFieldData,
    isTransportationData,
    isFuelAndEnergyData,
  } = typeGuards;
  const [isFarmCommodityModal, setIsFarmCommodityModal] = useState(false);
  const [commodityData, setCommodityData] = useState<Commodity>();
  const { submitHandlers, draftButtonLoading } = useSubmitHandlers(
    commodityId,
    commodityData?.locationId,
    commodityData?.harvestYear
  );

  useEffect(() => {
    fetchCommodityData();
  }, [location.pathname]);

  const fetchCommodityData = async () => {
    const specificCommodityData = await getCommodity(Number(commodityId));
    if (specificCommodityData) setCommodityData(specificCommodityData);
  };

  const stage1DefaultData = () =>
    stage1InitialData(Number(commodityId), getRawMaterials);
  const stage2DefaultData = () =>
    stage2InitialData(commodityId, fetchTransportationData);
  const stage3DefaultData = () =>
    stage3InitialData(commodityId, fetchFuelAndEnergyData);
  const stage4DefaultData = () =>
    stage4InitialData(commodityId, fetchFieldData);
  const stage5DefaultData = () =>
    stage5InitialData(Number(commodityId), getCommodityDistribution);

  const stage1Method = useForm<FarmboxRawMaterialsData>({
    defaultValues: stage1DefaultData,
    resolver: yupResolver(validationSchema),
    mode: 'all',
    reValidateMode: 'onBlur',
  });

  const stage2Method = useForm<TransportationDetails>({
    defaultValues: stage2DefaultData,
    resolver: yupResolver(farmTransportationValidationSchema),
    mode: 'all',
    reValidateMode: 'onBlur',
  });

  const stage3Method = useForm<FuelAndEnergyDetails>({
    defaultValues: stage3DefaultData,
    mode: 'all',
  });

  const stage4Method = useForm<FieldDetails>({
    defaultValues: stage4DefaultData,
    resolver: yupResolver(farmFieldValidationSchema),
    mode: 'all',
    reValidateMode: 'onBlur',
  });

  const stage5Method = useForm<FarmBoxCommodityDestribution>({
    defaultValues: stage5DefaultData,
    resolver: yupResolver(validationSchemaForCommodityDestribution),
    mode: 'all',
    reValidateMode: 'onBlur',
  });

  // THis is for dynamic form methods based on the stage
  const methods: UseFormReturn<FormDataTypes> = isRawMaterial
    ? (stage1Method as UseFormReturn<FormDataTypes>)
    : isTransportation
    ? (stage2Method as UseFormReturn<FormDataTypes>)
    : isFuelAndEnergy
    ? (stage3Method as UseFormReturn<FormDataTypes>)
    : isField
    ? (stage4Method as UseFormReturn<FormDataTypes>)
    : (stage5Method as UseFormReturn<FormDataTypes>);

  const {
    fuelAndEnergyDraftLoading,
    rawMaterialDraftLoading,
    transportationDraftLoading,
    fieldDraftLoading,
    commodityDistributionDraftLoading,
  } = draftButtonLoading;

  const uiHandlers = {
    toggleHelpDesk: () => setIsHelpDeskOpen(!isHelpDeskOpen),
    openInfoModal: () => setIsFarmCommodityModal(true),
    confirmModal: () => redirectToFarm(CommodityTabsKeys.DRAFT),
    getDraftLoading: () => {
      if (isTransportation) return transportationDraftLoading;
      if (isFuelAndEnergy) return fuelAndEnergyDraftLoading;
      if (isField) return fieldDraftLoading;
      if (isRawMaterial) return rawMaterialDraftLoading;
      return commodityDistributionDraftLoading;
    },
  };
  const handleSubmit = methods.handleSubmit((data: FormDataTypes) => {
    switch (true) {
      case isRawMaterialsData(data):
        return submitHandlers.stage1(data as FarmboxRawMaterialsData);

      case isTransportationData(data):
        return submitHandlers.stage2(data as TransportationDetails);

      case isFuelAndEnergyData(data):
        return submitHandlers.stage3();

      case isFieldData(data):
        return submitHandlers.stage4(data as FieldDetails);

      default:
        return submitHandlers.stage5(data as FarmBoxCommodityDestribution);
    }
  });

  const handleGetCommodity = () => fetchCommodityData();

  const footerItemsList = footerItems(location);

  return (
    <FormProvider {...methods}>
      <div className={styles.farmOutletContainer}>
        <Row>
          <Col span={1} className={styles.sidebarContainer}>
            <DashboardSidebar />
          </Col>
          <Col span={23}>
            {isNotOverview && (
              <HeaderForStages
                commodityName={commodity?.name ?? ''}
                infoCallback={uiHandlers.openInfoModal}
                handleCloseCancelModal={handleSubmit}
                handleConfirmCancelModal={uiHandlers.confirmModal}
                saveCallback={handleSubmit}
                draftLoading={uiHandlers.getDraftLoading()}
                loading={isLoading}
              />
            )}
            <div className={styles.outletContainer}>
              <Outlet />
            </div>
            {isNotOverview && (
              <>
                <div className={styles.footerContainer}>
                  <FooterForStages footerItems={footerItemsList} />
                </div>

                {/* Helpdesk functionality */}
                <div
                  className={clsx(styles.helpdeskContainer, {
                    [styles.openHelpDesk]: isHelpDeskOpen,
                    [styles.closedHelpDesk]: !isHelpDeskOpen,
                  })}>
                  <HelpDesk
                    commodity={commodity}
                    helpDeskData={helpDeskData}
                    loading={helpDeskLoading}
                    isCommodity
                  />
                </div>

                <div
                  className={clsx(styles.openHelpDeskButton, {
                    [styles.openButtonActive]: isHelpDeskOpen,
                  })}
                  onClick={uiHandlers.toggleHelpDesk}>
                  <p className={styles.helpDesk}>
                    <CaretLeftOutlined
                      className={clsx(
                        styles.helpDeskOpenIcon,
                        isHelpDeskOpen && styles.iconOpen
                      )}
                    />
                    {FlowContainerConstants.HELP_DESK}
                  </p>
                </div>
              </>
            )}
          </Col>
        </Row>
        {isFarmCommodityModal && (
          <FarmCommodity
            visible={isFarmCommodityModal}
            setVisible={setIsFarmCommodityModal}
            handleGetCommodity={handleGetCommodity}
            isRawMaterial={isRawMaterial}
          />
        )}
      </div>
    </FormProvider>
  );
};

export default FarmOutlet;
